@if (loading || !initialized) {
  <div class="flex h-full items-center justify-center gap-4 bg-white" [attr.aria-busy]="loading">
    @if (loading) {
      <app-icon class="animate-spin">progress_activity</app-icon>
    } @else {
      <app-button (onChangeClick)="loadFilterSets()">{{ 'board_v3_filter.load_failed' | translate }}<app-icon>refresh</app-icon></app-button>
      <button type="button" class="flex h-11 w-11 items-center justify-center" [attr.aria-label]="'close' | translate" (click)="closeChange.emit()"><app-icon>close</app-icon></button>
    }
  </div>
} @else {
<div class="flex h-full w-full flex-col overflow-hidden bg-white md:flex-row">
  <div class="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 md:hidden">
    <app-select
      class="min-w-0 flex-1"
      [fluid]="true"
      [options]="filterSets"
      optionLabel="name"
      optionValue="id"
      [showClear]="true"
      [placeholder]="'board_v3_filter.unsaved_filter' | translate"
      [ngModel]="selectedFilterId"
      (ngModelChange)="selectFilter($event)"
    />
    @if (selectedFilterId) {
      <button type="button" class="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-500 disabled:opacity-50" [disabled]="deleting" [attr.aria-label]="'delete' | translate" [matTooltip]="'delete' | translate" (click)="deleteSelected()">
        <app-icon>delete</app-icon>
      </button>
    }
    <button type="button" class="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100" [attr.aria-label]="'close' | translate" (click)="closeChange.emit()">
      <app-icon>close</app-icon>
    </button>
  </div>
  <aside class="flex min-h-0 shrink-0 flex-col border-r border-gray-200 bg-gray-50 max-md:hidden md:w-[280px] lg:w-[320px]">
    <div class="crm-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4">
      <button
        type="button"
        class="mb-1 block h-10 w-full truncate rounded-md! px-3 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-600"
        [class.bg-blue-100]="!selectedFilterId"
        [class.ring-1]="!selectedFilterId"
        [class.ring-inset]="!selectedFilterId"
        [class.ring-blue-300]="!selectedFilterId"
        [class.text-blue-600]="!selectedFilterId"
        [attr.aria-current]="!selectedFilterId ? 'true' : null"
        (click)="selectFilter(null)"
      >
        {{ 'board_v3_filter.unsaved_filter' | translate }}
      </button>

      @if (loading) {
        <app-icon class="!h-6 !w-6 animate-spin !text-[24px] text-gray-300">progress_activity</app-icon>
      } @else {
        <div class="flex flex-col gap-1">
          @for (filter of filterSets; track filter.id) {
            <div
              class="flex h-10 items-center gap-1 rounded-md! transition-colors hover:bg-blue-50"
              [class.bg-blue-100]="filter.id === selectedFilterId"
              [class.ring-1]="filter.id === selectedFilterId"
              [class.ring-inset]="filter.id === selectedFilterId"
              [class.ring-blue-300]="filter.id === selectedFilterId"
            >
              <button
                type="button"
                class="flex h-full min-w-0 flex-1 items-center gap-2 pl-3 pr-1 text-left text-sm font-medium text-gray-700 hover:text-blue-600"
                [class.text-blue-600]="filter.id === selectedFilterId"
                [attr.aria-current]="filter.id === selectedFilterId ? 'true' : null"
                [attr.title]="filter.name"
                (click)="selectFilter(filter.id)"
                (dblclick)="selectFilter(filter.id); applyDraft()"
              >
                <span class="min-w-0 flex-1 truncate">{{ filter.name }}</span>
                @if (filter.id === selectedFilterId && hasUnsavedChanges) {
                  <app-icon
                    class="!h-4 !w-4 shrink-0 !text-[16px] text-amber-600"
                    [matTooltip]="'unsaved_changes' | translate"
                    [attr.aria-label]="'unsaved_changes' | translate"
                    role="img"
                  >edit</app-icon>
                }
                @if (filter.is_shared) {
                  <app-icon
                    class="!h-4 !w-4 shrink-0 !text-[16px] text-gray-400"
                    [attr.title]="'board_v3_filter.for_all' | translate"
                    [attr.aria-label]="'board_v3_filter.for_all' | translate"
                    role="img"
                  >share</app-icon>
                }
              </button>

              <button
                type="button"
                class="mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg! text-gray-400 hover:bg-red-50 hover:text-red-500"
                [attr.aria-label]="'delete' | translate"
                [attr.title]="'delete' | translate"
                (click)="selectFilter(filter.id); deleteSelected()"
              >
                <app-icon class="!h-5 !w-5 !text-[20px]">close</app-icon>
              </button>
            </div>
          }
        </div>
      }
    </div>

    <div class="flex shrink-0 gap-2 px-4 py-2 md:flex-col md:py-4">
      <app-button
        btnType="cancel"
        customClass="bg-transparent! text-gray-600!"
        [full]="true"
        (onChangeClick)="resetDraft()"
      >
        <span class="inline-flex items-center justify-center gap-2 whitespace-nowrap">
          <app-icon class="!h-5 !w-5 shrink-0 !text-[20px]">restart_alt</app-icon>
          <span>{{ 'board_v3_filter.reset_filters' | translate }}</span>
        </span>
      </app-button>
      <app-button
        [full]="true"
        [disabled]="!canSave"
        (onChangeClick)="saveDraft()"
      >
        {{ (saving ? 'saving' : 'save') | translate }}
      </app-button>
    </div>
  </aside>

  <section class="flex min-h-0 min-w-0 flex-1 flex-col">
    <div class="crm-scroll flex-1 overflow-y-auto px-3 py-4 md:px-6">
      <div class="max-w-[820px]">
        <div class="mb-3 grid grid-cols-1 items-center gap-x-2 gap-y-2 md:grid-cols-[minmax(0,1fr)_44px]">
          <label for="board-filter-name" class="block text-sm font-medium text-gray-400 md:col-span-2">{{ 'filter_name' | translate }}</label>
          <app-input
            id="board-filter-name"
            class="block min-w-0"
            [(ngModel)]="draftName"
            [placeholder]="'enter_filter_name' | translate"
          />

          <button
            type="button"
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg! text-gray-400 hover:bg-gray-100 max-md:hidden"
            [attr.aria-label]="'close' | translate"
            (click)="closeChange.emit()"
          >
            <app-icon class="!h-6 !w-6 !text-[24px]">close</app-icon>
          </button>
        </div>

        <div class="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-[220px_1fr]">
          <div>
            <label for="board-filter-mode" class="mb-2 block text-sm font-medium text-gray-400">{{ 'board_v3_filter.mode' | translate }}</label>
            <app-select
              id="board-filter-mode"
              class="block min-w-0"
              [fluid]="true"
              [options]="modeOptions"
              optionLabel="label"
              optionValue="value"
              [isTranslate]="true"
              [(ngModel)]="mode"
            />
          </div>

          @if (entityMode === 'card') {
          <div>
            <span class="mb-2 block text-sm font-medium text-gray-400">
              {{ 'board_v3_filter.show_system_columns' | translate }}
            </span>

            <div class="flex min-h-11 flex-wrap items-center gap-x-4 gap-y-1">
              <app-checkbox [(ngModel)]="view.is_view_failed">
                {{ 'failed' | translate }}
              </app-checkbox>

              <app-checkbox [(ngModel)]="view.is_view_success">
                {{ 'success' | translate }}
              </app-checkbox>
            </div>
          </div>
          }
        </div>

        <div class="flex flex-col gap-3">
          @for (condition of conditions; track condition.localId) {
            @let field = fieldForCondition(condition);
            @let inputType = field?.filter_type === 'date' ? 'date' : field?.filter_type === 'number' ? 'number' : 'default';
            @let operator = operatorForCondition(condition);

            <div class="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div class="mb-1 flex items-center gap-2">
                <app-select
                  class="min-w-0 flex-1"
                  [fluid]="true"

                  [options]="fieldSelectOptions"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  [(ngModel)]="condition.key"
                  (ngModelChange)="onFieldChange(condition)"
                />

                <button
                  type="button"
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg! text-gray-400 hover:bg-red-50 hover:text-red-500"
                  [attr.aria-label]="'delete' | translate"
                  (click)="removeCondition(condition)"
                >
                  <app-icon class="!h-5 !w-5 !text-[20px]">close</app-icon>
                </button>
              </div>

              <div class="grid grid-cols-1 gap-2 sm:grid-cols-[220px_1fr]">
                <app-select
                  class="min-w-0"
                  [fluid]="true"
                  [options]="operatorsForCondition(condition)"
                  optionLabel="label"
                  optionValue="value"
                  [isTranslate]="true"
                  [(ngModel)]="condition.operator"
                  (ngModelChange)="onOperatorChange(condition)"
                />

                @if (operator?.requiresValue === false) {
                  <app-input
                    class="min-w-0"
                    [disabled]="true"
                    [placeholder]="'board_v3_filter.no_value' | translate"
                  />
                } @else if (operator?.isRange) {
                  <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <app-input
                      [inputType]="inputType"
                      [minFractionDigits]="0"
                      class="min-w-0"
                      [(ngModel)]="condition.value"
                      [placeholder]="'board_v3_filter.from' | translate"
                    />
                    <app-input
                      [inputType]="inputType"
                      [minFractionDigits]="0"
                      class="min-w-0"
                      [(ngModel)]="condition.valueTo"
                      [placeholder]="'board_v3_filter.to' | translate"
                    />
                  </div>
                } @else if (operator?.isMulti && field?.filter_type === 'manager') {
                  <app-select
                    class="min-w-0"
                    [fluid]="true"
                    [type]="SelectType.Multiple"
                    [options]="managerOptions"
                    [isAvatar]="true"
                    optionLabel="name"
                    optionValue="id"
                    [filter]="true"
                    [placeholder]="'select' | translate"
                    [(ngModel)]="condition.value"
                  />
                } @else if (operator?.isMulti && field?.filter_type === 'select') {
                  <app-select
                    class="min-w-0"
                    [fluid]="true"
                    [type]="SelectType.Multiple"
                    [options]="fieldValueOptions(field)"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    [placeholder]="'select' | translate"
                    [(ngModel)]="condition.value"
                  />
                } @else if (field?.filter_type === 'manager') {
                  <app-select
                    class="min-w-0"
                    [fluid]="true"
                    [options]="managerOptions"
                    [isAvatar]="true"
                    optionLabel="name"
                    optionValue="id"
                    [filter]="true"
                    [showClear]="true"
                    [placeholder]="'select' | translate"
                    [(ngModel)]="condition.value"
                  />
                } @else if (field?.filter_type === 'select') {
                  <app-select
                    class="min-w-0"
                    [fluid]="true"
                    [options]="fieldValueOptions(field)"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    [showClear]="true"
                    [placeholder]="'select' | translate"
                    [(ngModel)]="condition.value"
                  />
                } @else if (field?.filter_type === 'boolean') {
                  <app-select
                    class="min-w-0"
                    [fluid]="true"
                    [options]="booleanOptions"
                    optionLabel="label"
                    optionValue="value"
                    [isTranslate]="true"
                    [(ngModel)]="condition.value"
                  />
                } @else {
                  <app-input
                    [inputType]="inputType"
                    [minFractionDigits]="0"
                    class="min-w-0"
                    [(ngModel)]="condition.value"
                    [placeholder]="'board_v3_filter.value' | translate"
                  />
                }
              </div>
            </div>
          }
        </div>

        <div class="mt-3 flex flex-wrap gap-x-4 gap-y-2">
          <app-button btnType="no-border" customClass="bg-transparent! text-gray-700!" (onChangeClick)="addCondition()">
            <span class="inline-flex items-center justify-center gap-2 whitespace-nowrap">
              <app-icon class="!h-5 !w-5 shrink-0 !text-[20px]">add</app-icon>
              <span>{{ 'add_field' | translate }}</span>
            </span>
          </app-button>

        </div>
        <div class="mt-4 md:hidden">
          <app-checkbox [(ngModel)]="draftShared">{{ 'share' | translate }}</app-checkbox>
        </div>
      </div>
    </div>

    <footer class="flex shrink-0 items-center justify-between gap-2 border-t border-gray-100 px-3 py-2 md:flex-wrap md:px-5 md:py-4">
      <app-checkbox class="block max-md:hidden" [(ngModel)]="draftShared">{{ 'share' | translate }}</app-checkbox>

      <div class="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_44px] items-center gap-2 md:flex md:w-auto md:flex-wrap">
        <app-button
          class="min-w-0 md:hidden"
          [full]="true"
          customClass="!min-w-0 !px-2 !tracking-normal"
          [disabled]="!canSave"
          (onChangeClick)="saveDraft()"
        >{{ (saving ? 'saving' : 'save') | translate }}</app-button>
        <app-button
          class="min-w-0"
          [full]="true"
          customClass="max-md:!min-w-0 max-md:!px-2 max-md:!tracking-normal"
          (onChangeClick)="applyDraft()"
        >
          <span class="inline-flex items-center justify-center gap-2 whitespace-nowrap">
            <app-icon class="!h-6 !w-6 shrink-0 !text-[24px] max-md:hidden">search</app-icon>
            <span>{{ 'search' | translate }}</span>
          </span>
        </app-button>

        <button type="button" class="flex h-11 w-11 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 md:hidden" [attr.aria-label]="'reset' | translate" [matTooltip]="'reset' | translate" (click)="resetDraft()">
          <app-icon>restart_alt</app-icon>
        </button>
      </div>
    </footer>
  </section>
</div>
}
